import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, RotateCcw, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { DocumentExportActions } from './DocumentExportActions';
import { getItemAssetPairs, ItemAssetPairs } from './ItemAssetPairs';
import { recalculateLineItem } from './lineItemCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SaleInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [warranties, setWarranties] = useState([]);

  // Conversion modal state
  const [showConvert, setShowConvert] = useState(false);
  const [convertItems, setConvertItems] = useState([]);
  const [convertSubmitting, setConvertSubmitting] = useState(false);
  const [convertError, setConvertError] = useState('');

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [docRes, prodRes, unitRes, taxRes, warrantyRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/sale-invoices/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/warranties`, getAuthHeaders())
        ]);
        setDoc(docRes.data);
        setProducts(prodRes.data);
        setUnits(unitRes.data);
        setTaxes(taxRes.data);
        setWarranties(warrantyRes.data || []);
      } catch (err) { setError('Failed to load sale invoice'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const getName = (list, id, field = 'name') => list.find(i => i.id === id)?.[field] || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const getWarrantyName = (item) => item.warranty_name
    || warranties.find(warranty => warranty.id === item.warranty_id)?.name
    || (item.warranty_days ? `${item.warranty_days} days` : '-');

  // --- Conversion Logic ---
  const openConvertModal = () => {
    const items = (doc.line_items || []).map(li => ({
      product_id: li.product_id || '',
      description: li.description || '',
      mrp: li.mrp || '',
      quantity: li.quantity || 1,
      max_quantity: li.quantity || 1,
      unit_id: li.unit_id || '',
      price_per_unit: li.price_per_unit || 0,
      discount_percentage: li.discount_percentage || 0,
      discount_amount: li.discount_amount || 0,
      tax_id: li.tax_id || '',
      tax_amount: li.tax_amount || 0,
      total_amount: li.total_amount || 0,
      warranty_id: li.warranty_id || '',
      warranty_name: li.warranty_name || '',
      item_ids: li.sold_item_ids || [],
      asset_ids: li.asset_ids || [],
    }));
    setConvertItems(items);
    setConvertError('');
    setShowConvert(true);
  };

  const recalcItem = (item, changedField = null) => recalculateLineItem(item, taxes, changedField);

  const updateConvertItem = (idx, field, value) => {
    setConvertItems(prev => {
      const n = [...prev];
      n[idx] = { ...n[idx], [field]: value };
      if (['quantity', 'price_per_unit', 'discount_percentage', 'discount_amount', 'tax_id'].includes(field)) {
        n[idx] = recalcItem(n[idx], field);
      }
      return n;
    });
  };

  const removeConvertItem = (idx) => {
    setConvertItems(prev => prev.filter((_, i) => i !== idx));
  };

  const convertTotals = useMemo(() => {
    let subtotal = 0;
    convertItems.forEach(i => { subtotal += (parseFloat(i.total_amount) || 0); });
    const total = Math.round(subtotal);
    const roundOffAmt = Math.round((total - subtotal) * 100) / 100;
    return { subtotal: Math.round(subtotal * 100) / 100, total, roundOffAmt };
  }, [convertItems]);

  const handleConvertSubmit = async (e) => {
    e.preventDefault();
    setConvertError('');
    if (!convertItems.length) { setConvertError('Add at least one item for the return'); return; }
    setConvertSubmitting(true);
    try {
      const lineItems = convertItems.map(li => {
        const quantity = Math.max(1, Math.floor(parseFloat(li.quantity) || 1));
        return {
          product_id: li.product_id || null,
          description: li.description || null,
          mrp: parseFloat(li.mrp) || null,
          quantity: parseFloat(li.quantity) || 1,
          unit_id: li.unit_id || null,
          price_per_unit: parseFloat(li.price_per_unit) || 0,
          discount_percentage: parseFloat(li.discount_percentage) || 0,
          discount_amount: parseFloat(li.discount_amount) || 0,
          tax_id: li.tax_id || null,
          tax_amount: parseFloat(li.tax_amount) || 0,
          total_amount: parseFloat(li.total_amount) || 0,
          warranty_id: li.warranty_id || null,
          warranty_name: li.warranty_name || null,
          item_ids: (li.item_ids || []).slice(0, quantity),
          asset_ids: (li.asset_ids || []).slice(0, quantity),
        };
      });

      const today = new Date().toISOString().slice(0, 10);
      const prefill = {
        source_invoice_id: doc.id,
        source_invoice_number: doc.invoice_number || '',
        customer_id: doc.customer_id || null,
        company_id: doc.company_id || null,
        invoice_number: doc.invoice_number || '',
        invoice_date: doc.invoice_date || '',
        date: today,
        state_of_supply: doc.state_of_supply || '',
        line_items: lineItems,
        description: `Sale Return against Invoice ${doc.invoice_number || ''}`.trim(),
        round_off: true,
      };
      navigate('/admin/sale/return/new', { state: { fromInvoice: prefill } });
    } catch (err) {
      setConvertError(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error) return <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>;
  if (!doc) return null;

  const exportConfig = {
    title: 'Sale Invoice', number: doc.invoice_number,
    metadata: [
      { label: 'Invoice Number', value: doc.invoice_number || '-' }, { label: 'Invoice Date', value: formatDate(doc.invoice_date) },
      { label: 'Customer', value: doc.customer_name || '-' }, { label: 'Company', value: doc.company_name || '-' },
      { label: 'Sale Type', value: doc.sale_type || '-' }, { label: 'State of Supply', value: doc.state_of_supply || '-' },
    ],
    columns: [
      { label: '#', value: (_, index) => index + 1, type: 'number' }, { label: 'Product', value: item => getName(products, item.product_id) },
      { label: 'Item ID / Asset ID', value: item => getItemAssetPairs({ item_details: item.item_details, itemIds: item.sold_item_ids, assetIds: item.asset_ids })
        .map(pair => `Item ID: ${pair.item_id || '-'}\nAsset ID: ${pair.asset_id || '-'}`).join('\n\n') || '-' },
      { label: 'Warranty', value: item => getWarrantyName(item) },
      { label: 'Description', value: item => item.description || '-' }, { label: 'MRP', value: item => Number(item.mrp || 0), type: 'number' },
      { label: 'Qty', value: item => Number(item.quantity || 0), type: 'number' }, { label: 'Unit', value: item => getName(units, item.unit_id) },
      { label: 'Price/Unit', value: item => Number(item.price_per_unit || 0), type: 'number' }, { label: 'Disc %', value: item => Number(item.discount_percentage || 0), type: 'number' },
      { label: 'Disc Amt', value: item => Number(item.discount_amount || 0), type: 'number' }, { label: 'Tax', value: item => item.tax_id ? getName(taxes, item.tax_id) : '-' },
      { label: 'Tax Amt', value: item => Number(item.tax_amount || 0), type: 'number' }, { label: 'Amount', value: item => Number(item.total_amount || 0), type: 'number' },
    ],
    rows: doc.line_items || [],
    totals: [
      { label: 'Subtotal', value: `INR ${Number(doc.subtotal || 0).toFixed(2)}`, rawValue: Number(doc.subtotal || 0) },
      { label: 'Total', value: `INR ${Number(doc.total || 0).toFixed(2)}`, rawValue: Number(doc.total || 0) },
      { label: 'Credit Notes Applied', value: `INR ${Number((doc.credit_note_allocations || []).reduce((sum, item) => sum + Number(item.amount || 0), 0)).toFixed(2)}`, rawValue: Number((doc.credit_note_allocations || []).reduce((sum, item) => sum + Number(item.amount || 0), 0)) },
      { label: 'Received', value: `INR ${Number(doc.received_amount || 0).toFixed(2)}`, rawValue: Number(doc.received_amount || 0) },
      { label: 'Balance', value: `INR ${Number(doc.balance || 0).toFixed(2)}`, rawValue: Number(doc.balance || 0) },
    ],
    shareLines: [`Customer: ${doc.customer_name || '-'}`, `Date: ${formatDate(doc.invoice_date)}`, `Total: INR ${Number(doc.total || 0).toFixed(2)}`, `Balance: INR ${Number(doc.balance || 0).toFixed(2)}`],
  };

  return (
    <div data-testid="sale-invoice-detail-page">
      <button onClick={() => navigate('/admin/sale/invoices')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors print:hidden">
        <ArrowLeft className="h-4 w-4" /> Back to Sale Invoices
      </button>

      {/* Header + Convert Button */}
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Sale Invoice Details</h1>
          {doc.status === 'Void' && (
            <span className="px-3 py-1 text-xs font-bold uppercase rounded bg-red-100 text-red-700" data-testid="void-badge">Void</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <DocumentExportActions config={exportConfig} testIdPrefix="sale-invoice" />
          {doc.status !== 'Void' && !doc.has_credit_note && !doc.is_internal_transfer && (
            <button onClick={openConvertModal} data-testid="convert-credit-note-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-red-700 transition-colors">
              <RotateCcw className="h-3.5 w-3.5" /> Convert to Sale Return / Credit Note
            </button>
          )}
          {doc.has_credit_note && (
            <span className="inline-flex items-center px-3 py-1.5 text-xs font-bold uppercase rounded bg-amber-100 text-amber-700" data-testid="returned-badge">
              Returned / Credit Note Issued
            </span>
          )}
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Invoice Number</span><span className="text-sm font-semibold text-slate-900">{doc.invoice_number || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Invoice Date</span><span className="text-sm text-slate-900">{formatDate(doc.invoice_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Sale Type</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${doc.sale_type === 'Credit' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>{doc.sale_type}</span>
          </div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Customer</span><span className="text-sm text-slate-900">{doc.customer_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Company</span><span className="text-sm text-slate-900">{doc.company_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">State of Supply</span><span className="text-sm text-slate-900">{doc.state_of_supply || '-'}</span></div>
          {doc.po_number && <div><span className="block text-xs font-medium text-slate-500 uppercase">PO Number</span><span className="text-sm text-slate-900">{doc.po_number}</span></div>}
          {doc.lut_arn_number && <div><span className="block text-xs font-medium text-slate-500 uppercase">LUT/ARN Number</span><span className="text-sm text-slate-900">{doc.lut_arn_number}</span></div>}
          {doc.billing_address && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Billing Address</span><span className="text-sm text-slate-900">{doc.billing_address}</span></div>}
          {doc.description && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Description</span><span className="text-sm text-slate-900">{doc.description}</span></div>}
          {doc.void_reason && <div className="col-span-2"><span className="block text-xs font-medium text-red-500 uppercase">Void Reason</span><span className="text-sm text-red-700">{doc.void_reason}</span></div>}
        </div>
      </div>

      {(doc.credit_note_allocations || []).length > 0 && (
        <div className="bg-white rounded-sm border border-amber-200 mb-6">
          <div className="px-6 py-4 border-b border-amber-200 bg-amber-50"><h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Credit Notes Applied</h2></div>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase"><th className="px-4 py-3 text-left">Credit Note</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-right">Amount</th></tr></thead>
            <tbody>{doc.credit_note_allocations.map((allocation, index) => (
              <tr key={allocation.allocation_id || index} className="border-b border-slate-100"><td className="px-4 py-3"><button onClick={() => navigate(`/admin/sale/return/${allocation.note_id}`)} className="font-semibold text-blue-600 hover:underline">{allocation.note_number || allocation.note_id}</button></td><td className="px-4 py-3 text-slate-600">{formatDate(allocation.allocated_at || allocation.note_date)}</td><td className="px-4 py-3 text-right font-semibold">₹{Number(allocation.amount || 0).toLocaleString('en-IN')}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Item ID / Asset ID</th>
                <th className="px-4 py-3 text-left">Warranty</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-right">MRP</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">Price/Unit</th>
                <th className="px-4 py-3 text-right">Disc %</th>
                <th className="px-4 py-3 text-right">Disc Amt</th>
                <th className="px-4 py-3 text-left">Tax</th>
                <th className="px-4 py-3 text-right">Tax Amt</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                  <td className="px-4 py-3">
                    <ItemAssetPairs item_details={li.item_details} itemIds={li.sold_item_ids} assetIds={li.asset_ids} testId={`item-assets-${idx}`} />
                  </td>
                  <td className="px-4 py-3 text-slate-700">{getWarrantyName(li)}</td>
                  <td className="px-4 py-3 text-slate-600">{li.description || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.mrp || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.quantity}</td>
                  <td className="px-4 py-3">{getName(units, li.unit_id)}</td>
                  <td className="px-4 py-3 text-right">{li.price_per_unit?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{li.discount_percentage}%</td>
                  <td className="px-4 py-3 text-right">{li.discount_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                  <td className="px-4 py-3 text-right">{li.tax_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-medium">{li.total_amount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium">₹{(doc.subtotal || 0).toFixed(2)}</span></div>
          {doc.round_off && <div className="flex justify-between text-sm"><span className="text-slate-500">Round Off</span><span>{(doc.round_off_amount || 0) >= 0 ? '+' : ''}{(doc.round_off_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Total</span><span>₹{(doc.total || 0).toLocaleString()}</span></div>
          {(doc.credit_note_allocations || []).length > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Credit Notes Applied</span><span className="text-amber-700 font-medium">₹{doc.credit_note_allocations.reduce((sum, item) => sum + Number(item.amount || 0), 0).toLocaleString('en-IN')}</span></div>}
          <div className="flex justify-between text-sm"><span className="text-slate-500">Received</span><span className="text-green-600 font-medium">₹{(doc.received_amount || 0).toLocaleString()}</span></div>
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Balance</span><span className={(doc.balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}>₹{(doc.balance || 0).toLocaleString()}</span></div>
        </div>
      </div>

      {/* ========== CONVERSION MODAL ========== */}
      {showConvert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-5xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
                Convert to Sale Return / Credit Note
              </h2>
              <button onClick={() => setShowConvert(false)} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleConvertSubmit} className="p-6">
              {convertError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
                  <AlertCircle className="h-5 w-5" /><span>{convertError}</span>
                </div>
              )}

              {/* Source info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                <div><span className="text-xs text-slate-500 block">Customer</span><span className="font-medium text-slate-900">{doc.customer_name || '-'}</span></div>
                <div><span className="text-xs text-slate-500 block">Company</span><span className="font-medium text-slate-900">{doc.company_name || '-'}</span></div>
                <div><span className="text-xs text-slate-500 block">Source Invoice</span><span className="font-medium text-slate-900">{doc.invoice_number || '-'}</span></div>
                <div><span className="text-xs text-slate-500 block">State of Supply</span><span className="font-medium text-slate-900">{doc.state_of_supply || '-'}</span></div>
              </div>

              <p className="text-xs text-slate-500 mb-3">Adjust return quantities or remove items you don&apos;t want to return. Quantity cannot exceed original invoice quantity.</p>

              {/* Editable Line Items */}
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                      <th className="px-2 py-2 text-left w-8">#</th>
                      <th className="px-2 py-2 text-left min-w-[180px]">Product</th>
                      <th className="px-2 py-2 text-left min-w-[120px]">Description</th>
                      <th className="px-2 py-2 text-right w-20">Orig Qty</th>
                      <th className="px-2 py-2 text-right w-24">Return Qty</th>
                      <th className="px-2 py-2 text-left w-24">Unit</th>
                      <th className="px-2 py-2 text-right w-24">Price/Unit</th>
                      <th className="px-2 py-2 text-right w-16">Disc %</th>
                      <th className="px-2 py-2 text-right w-24">Disc Amt</th>
                      <th className="px-2 py-2 text-left w-32">Tax</th>
                      <th className="px-2 py-2 text-right w-24">Amount</th>
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {convertItems.map((li, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="px-2 py-2 text-slate-400">{idx + 1}</td>
                        <td className="px-2 py-2 font-medium text-slate-900 text-sm">
                          {getName(products, li.product_id)}
                        </td>
                        <td className="px-2 py-2 text-slate-600 text-sm">{li.description || '-'}</td>
                        <td className="px-2 py-2 text-right text-slate-500">{li.max_quantity}</td>
                        <td className="px-2 py-2">
                          <input type="number" value={li.quantity}
                            onChange={e => {
                              let val = parseFloat(e.target.value);
                              if (val > li.max_quantity) val = li.max_quantity;
                              updateConvertItem(idx, 'quantity', val);
                            }}
                            min="1" max={li.max_quantity} step="1" data-testid={`conv-qty-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-2 py-2 text-sm text-slate-600">{getName(units, li.unit_id)}</td>
                        <td className="px-2 py-2 text-right text-sm">{(parseFloat(li.price_per_unit) || 0).toFixed(2)}</td>
                        <td className="px-2 py-2">
                          <input type="number" value={li.discount_percentage} onChange={e => updateConvertItem(idx, 'discount_percentage', e.target.value)}
                            min="0" max="100" step="0.01" data-testid={`conv-discount-percent-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={li.discount_amount} onChange={e => updateConvertItem(idx, 'discount_amount', e.target.value)}
                            min="0" step="0.01" data-testid={`conv-discount-amount-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-2 py-2 text-sm">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                        <td className="px-2 py-2 text-right font-medium text-slate-900" data-testid={`conv-total-${idx}`}>
                          {(parseFloat(li.total_amount) || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-2">
                          <button type="button" onClick={() => removeConvertItem(idx)} data-testid={`conv-remove-${idx}`}
                            className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                    {convertItems.length === 0 && (
                      <tr><td colSpan={12} className="text-center py-6 text-slate-400">All items removed. Add at least one item to proceed.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="border-t border-slate-200 pt-4 mb-4">
                <div className="max-w-xs ml-auto space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Return Subtotal</span><span className="font-medium">₹{convertTotals.subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Round Off</span><span>{convertTotals.roundOffAmt >= 0 ? '+' : ''}{convertTotals.roundOffAmt.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold border-t border-slate-200 pt-2"><span>Credit Note Total</span><span>₹{convertTotals.total.toLocaleString()}</span></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                <button type="button" onClick={() => setShowConvert(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={convertSubmitting || !convertItems.length} data-testid="conv-submit-btn"
                  className="px-6 py-2 bg-red-600 text-white font-bold uppercase tracking-wider rounded-none hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2">
                  {convertSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create Credit Note
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
